/**
 * Sender: Bildschirm aufnehmen, kachelweise vergleichen, Atlanten verschicken.
 */

import {
  KEYFRAME_RATIO,
  LOSSY_QUALITY,
  SENDER_ALIVE_INTERVAL,
  TEXT_QUALITY,
  TILE_DIM,
} from '../../shared/constants.ts';
import {
  type ImageMime,
  type LosslessMime,
  type ServerMessage,
  encodeDiffFrame,
  encodeKeyFrame,
} from '../../shared/protocol.ts';
import { ServerClock } from '../../shared/clock.ts';
import { AtlasPacker, detectTextFormat } from './atlas.ts';
import { type CaptureFrame, ScreenCapture } from './capture.ts';
import { type ClassifyMode, Tiler } from './tiler.ts';

/** Ab diesem Sendepuffer wird ein Frame verworfen statt eingereiht. */
const BUFFER_LIMIT = 2 << 20;

const params = new URLSearchParams(location.search);
const room = params.get('room') ?? 'test';

/**
 * Positive Ganzzahl aus der Adresszeile, sonst null.
 *
 * Vorsicht bei fehlendem Parameter: get() liefert null, und Number(null) ist 0
 * – nicht NaN. Ohne die Prüfung auf null gälte jeder Aufruf ohne Parameter als
 * gesetzte 0.
 */
function numberParam(name: string): number | null {
  const raw = params.get(name);
  if (raw === null) return null;
  const value = Math.floor(Number(raw));
  return Number.isFinite(value) && value > 0 ? value : null;
}

const ui = {
  start: document.querySelector<HTMLButtonElement>('#start')!,
  stop: document.querySelector<HTMLButtonElement>('#stop')!,
  mode: document.querySelector<HTMLSelectElement>('#mode')!,
  status: document.querySelector<HTMLElement>('#status')!,
  stats: document.querySelector<HTMLElement>('#stats')!,
  viewers: document.querySelector<HTMLElement>('#viewers')!,
  preview: document.querySelector<HTMLCanvasElement>('#preview')!,
};

let socket: WebSocket | null = null;
/** Uhr des Servers: Die Zeitstempel der Frames sind die Bezugsgröße der
 * Latenzmessung beim Zuschauer und müssen aus derselben Uhr stammen. */
const clock = new ServerClock(send);
let tiler: Tiler | null = null;
/** Format des Text-Atlas; steht erst nach detectTextFormat() fest. */
let textMime: LosslessMime = 'image/png';
/** Bilder, die wegen voller Leitung bzw. laufender Kodierung nicht erfasst wurden. */
let dropCongested = 0;
let dropBusy = 0;
let sentBytes = 0;
let frameCount = 0;
let lastStatsAt = performance.now();
let fpsCounter = 0;
let lastAliveAt = 0;

const measured = {
  width: 0,
  height: 0,
  tiles: 0,
  lossyBytes: 0,
  losslessBytes: 0,
  captureMs: 0,
  diffMs: 0,
  encodeMs: 0,
  commitMs: 0,
  previewMs: 0,
  cycleMs: 0,
  keyframes: 0,
};

/** Beginn des vorigen Durchlaufs – daraus ergibt sich der tatsächliche Takt. */
let lastCycleAt = 0;

/** Die Vorschau muss nicht im Bildtakt laufen; das Auge merkt es nicht. */
const PREVIEW_INTERVAL = 200;
let lastPreviewAt = 0;

/**
 * Kodierung und Aufnahme laufen überlappend: Während ein Frame kodiert wird,
 * kann das nächste schon erfasst und verglichen werden. Zwei Dinge müssen
 * dabei gewahrt bleiben – die Reihenfolge der Frames (der Empfänger baut
 * Deltas aufeinander auf) und eine Obergrenze, damit sich bei langsamer
 * Kodierung keine Warteschlange aufbaut.
 *
 * Zwei gleichzeitige Kodierungen sind gemessen deutlich besser als eine: Der
 * Browser kodiert sie echt parallel, die einzelne Kodierung wird dabei nur um
 * ein Zehntel langsamer (174 → 191 ms), der Durchsatz aber fast doppelt so
 * hoch (4,4 → 7,8 fps bei 3100 Kacheln in 4K). Der Preis ist die Wartezeit je
 * Frame, die beim Zuschauer als Latenz ankommt. Über ?inflight=N lässt sich
 * ohne Neubau vergleichen, auch nach oben.
 */
const MAX_IN_FLIGHT = Math.min(4, numberParam('inflight') ?? 2);
let inFlight = 0;
/** Kette der ausstehenden Sendevorgänge – hält die Frames in Reihenfolge. */
let sendChain: Promise<void> = Promise.resolve();

/**
 * Ein Packer je gleichzeitig kodiertem Frame: Der eine kodiert womöglich noch,
 * während der andere schon das nächste Bild zusammenstellt.
 *
 * Genommen wird ein freier, nicht reihum der nächste. Die Kodierungen enden
 * nicht zwingend in der Reihenfolge, in der sie begonnen haben – scheitert
 * eine, gibt sie ihren Platz sogar vor ihrem Vorgänger frei. Ein Zeiger, der
 * bloß hochzählt, zeigte danach auf einen Packer, aus dem noch kodiert wird.
 *
 * Je Platz zwei Packer, einer für jede Kachelklasse: Beide Atlanten eines
 * Frames werden gleichzeitig gebaut, und ein gemeinsames Canvas würde vom
 * zweiten in der Größe verändert und neu bemalt, während der erste noch
 * kodiert. Laut Spezifikation nimmt convertToBlob sein Abbild sofort – darauf
 * muss man sich für zwei Zeilen Ersparnis aber nicht verlassen.
 */
const packers: { lossy: AtlasPacker; lossless: AtlasPacker; busy: boolean }[] = [];

/** Zählt die Auflösungswechsel; Frames einer älteren Generation gehen nicht raus. */
let geometry = 0;

const capture = new ScreenCapture(onFrame);

// Auflösungsdeckel zum Vergleichen umstellbar; wirkt beim nächsten Start.
const maxEdgeParam = numberParam('maxedge');
if (maxEdgeParam !== null) capture.maxEdge = maxEdgeParam;

capture.onResize = (width, height) => {
  // Ein Auflösungswechsel macht alles ungültig, was noch in der Sendekette
  // hängt: Diese Frames beschreiben Kacheln in der alten Rasterung, der Server
  // legt sie aber schon in der neuen aus. Die Generation steigt, ihre Frames
  // fallen unten heraus.
  geometry++;
  tiler = new Tiler(width, height);
  packers.length = 0;
  // Je gleichzeitig kodiertem Frame ein eigenes Canvas – sonst überschriebe ein
  // Frame den Atlas, aus dem gerade noch kodiert wird.
  for (let i = 0; i < MAX_IN_FLIGHT; i++) {
    packers.push({
      lossy: new AtlasPacker(document.createElement('canvas'), width, height, tiler.tilesX),
      lossless: new AtlasPacker(document.createElement('canvas'), width, height, tiler.tilesX),
      busy: false,
    });
  }
  send({ t: 'meta', width, height, tileDim: TILE_DIM });
};

// Vor der Aufnahme fragen, statt hinterher zu verwerfen: Ein Bild, das die
// Pipeline nicht annehmen kann, soll gar nicht erst ausgelesen werden.
capture.canAccept = () => {
  // Auch ein abgelehntes Bild ist ein Lebenszeichen: Der Bildschirm läuft, es
  // stockt nur gerade die Kodierung oder die Leitung. Ohne diese Meldung wäre
  // der Sender für den Server genau dann verstummt, wenn es eng wird – und
  // vier Sekunden später gälte die Freigabe als abgebrochen.
  noteAlive();

  if (inFlight >= MAX_IN_FLIGHT) {
    dropBusy++;
    return false;
  }
  if ((socket?.bufferedAmount ?? 0) > BUFFER_LIMIT) {
    dropCongested++;
    return false;
  }
  return true;
};

// Ende der Freigabe – egal ob über den Knopf oder die Leiste des Browsers.
capture.onStop = () => {
  send({ t: 'sharing', active: false });
  ui.start.disabled = false;
  ui.stop.disabled = true;
  setStatus('gestoppt');
};

// ---------------------------------------------------------------------------
// Verbindung
// ---------------------------------------------------------------------------

function connect(): void {
  const url = new URL('/ws', location.href);
  url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  socket = new WebSocket(url);
  socket.binaryType = 'arraybuffer';

  socket.addEventListener('open', () => {
    send({ t: 'hello', role: 'sender', room });
    clock.start();
    if (capture.active) {
      // Nach einem Reconnect gilt der Raum als leer: Freigabe erneut anmelden
      // und die Vergleichsbasis verwerfen, damit ein Vollbild folgt.
      send({ t: 'sharing', active: true });
      tiler?.invalidate();
      setStatus(`teilt ${capture.width}×${capture.height}`);
      return;
    }
    setStatus('verbunden');
  });

  socket.addEventListener('message', (event) => {
    if (typeof event.data !== 'string') return;
    const message = JSON.parse(event.data) as ServerMessage;

    if (message.t === 'time') {
      clock.accept(message);
    } else if (message.t === 'requestKeyframe') {
      // Vergleichsbasis verwerfen: das nächste Frame enthält dann alles.
      tiler?.invalidate();
    } else if (message.t === 'setRate') {
      capture.fps = message.fps;
    } else if (message.t === 'viewerCount') {
      ui.viewers.textContent = `${message.count} Zuschauer`;
    } else if (message.t === 'stats') {
      renderViewerStats(message.viewers);
    }
  });

  socket.addEventListener('close', () => {
    clock.stop();
    clock.reset();
    setStatus('getrennt – neuer Versuch in 2 s');
    socket = null;
    setTimeout(connect, 2000);
  });
}

function send(message: object): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
}

/**
 * Lebenszeichen, gekoppelt an den Aufnahmetakt.
 *
 * Bei unverändertem Bildschirm geht kein Frame raus – ohne dieses Signal wäre
 * ein stehendes Bild nicht von einem toten Sender zu unterscheiden. Ein Timer
 * taugt dafür nicht: In einem verdeckten Tab wird er gedrosselt, das Signal
 * käme also gerade dann zu selten, wenn es gebraucht wird. Gebunden an die
 * Aufnahme sagt es zudem das Richtige aus – nämlich dass wirklich noch Bilder
 * entstehen, nicht bloß, dass die Seite existiert.
 */
function noteAlive(): void {
  const now = performance.now();
  if (now - lastAliveAt < SENDER_ALIVE_INTERVAL) return;
  lastAliveAt = now;
  send({ t: 'alive' });
}

// ---------------------------------------------------------------------------
// Bildverarbeitung
// ---------------------------------------------------------------------------

async function onFrame(frame: CaptureFrame): Promise<void> {
  // Vor allen Abbruchgründen: Ein erfasstes Bild ist das Lebenszeichen.
  noteAlive();

  // Abstand zweier Aufnahmen – die Zahl, an der sich alles andere messen muss.
  const cycleStart = performance.now();
  if (lastCycleAt > 0) measured.cycleMs = cycleStart - lastCycleAt;
  lastCycleAt = cycleStart;
  measured.captureMs = frame.captureMs;
  measured.width = frame.width;
  measured.height = frame.height;

  if (!tiler || packers.length === 0 || socket?.readyState !== WebSocket.OPEN) return;

  const mode = ui.mode.value as ClassifyMode;
  // Wozu dieses Frame gehört – geprüft wird das erst kurz vor dem Senden,
  // denn der Wechsel kann auch noch während der Kodierung kommen.
  const generation = geometry;

  const t0 = performance.now();
  const diff = tiler.diff(frame.data, mode);
  const t1 = performance.now();

  const changed = diff.lossy.length + diff.lossless.length;
  if (changed === 0) return;

  // Den Platz vor dem commit belegen: Ohne freien Packer wird dieses Bild
  // ausgelassen, und dann darf auch die Vergleichsbasis nicht weiterrücken.
  // Vorkommen sollte das nicht – canAccept weist schon bei MAX_IN_FLIGHT ab.
  const slot = packers.find((entry) => !entry.busy);
  if (!slot) {
    dropBusy++;
    return;
  }
  slot.busy = true;

  // Die Vergleichsbasis wird jetzt fortgeschrieben, nicht erst nach dem Senden:
  // Sonst vergliche das nächste Bild noch gegen den vorvorigen Stand und
  // übertrüge dieselben Kacheln ein zweites Mal. Scheitert das Senden, wird
  // die Basis unten verworfen und das nächste Frame kommt als Vollbild.
  const t2 = performance.now();
  tiler.commit(frame.data);
  measured.commitMs = performance.now() - t2;

  slot.lossy.setSource(frame.canvas, frame.width, frame.height, tiler.tilesX);
  slot.lossless.setSource(frame.canvas, frame.width, frame.height, tiler.tilesX);

  // Der Aufbau startet hier synchron – alles, was aus dem Aufnahme-Canvas
  // gebraucht wird, ist damit kopiert, bevor das nächste Bild hineinläuft.
  const encodeStart = performance.now();
  const building = buildPayload(frame, diff, changed, tiler.tileCount, slot);

  measured.tiles = changed;
  measured.diffMs = t1 - t0;

  inFlight++;
  const previous = sendChain;
  sendChain = (async () => {
    try {
      const payload = await building;
      measured.encodeMs = performance.now() - encodeStart;
      // Erst senden, wenn der Vorgänger draußen ist: Der Empfänger setzt die
      // Deltas aufeinander auf, eine Vertauschung würde ihn verwirren.
      await previous;

      if (generation !== geometry) {
        // Inzwischen hat sich die Auflösung geändert. Kein invalidate nötig:
        // Der Tiler ist mit der neuen Geometrie ohnehin neu und ohne Basis,
        // das nächste Frame wird also ein Vollbild.
        return;
      }
      if (socket?.readyState !== WebSocket.OPEN) {
        tiler?.invalidate();
        return;
      }
      socket.send(payload);
      sentBytes += payload.length;
      frameCount++;
      fpsCounter++;
      updateStats();
    } catch (err) {
      console.error('[sender] Kodieren fehlgeschlagen:', err);
      // Die Basis ist fortgeschrieben, das Frame aber nie angekommen –
      // nur ein Vollbild bringt Sender und Empfänger wieder zusammen.
      tiler?.invalidate();
    } finally {
      slot.busy = false;
      inFlight--;
    }
  })();

  drawPreview(frame);
}

/**
 * Baut das Sendepaket. Der Anfang läuft synchron, damit die Kacheln aus dem
 * Aufnahme-Canvas kopiert sind, bevor der Aufrufer zurückkehrt.
 */
async function buildPayload(
  frame: CaptureFrame,
  diff: { lossy: number[]; lossless: number[] },
  changed: number,
  tileCount: number,
  packer: { lossy: AtlasPacker; lossless: AtlasPacker },
): Promise<Uint8Array> {
  // In Serverzeit, nicht in der eigenen: Der Zuschauer rechnet mit derselben.
  const timestamp = clock.now();

  if (changed > tileCount * KEYFRAME_RATIO) {
    // Fast alles geändert: ein Vollbild ist kompakter als ein Atlas, dessen
    // Zellen durch das Padding mehr Fläche belegen (ATLAS_PADDING).
    const useLossless = diff.lossless.length >= diff.lossy.length;
    const mime: ImageMime = useLossless ? textMime : 'image/webp';
    const blob = await frame.canvas.convertToBlob({
      type: mime,
      quality: useLossless ? TEXT_QUALITY : LOSSY_QUALITY,
    });
    const data = new Uint8Array(await blob.arrayBuffer());
    measured.keyframes++;
    measured.lossyBytes = useLossless ? 0 : data.length;
    measured.losslessBytes = useLossless ? data.length : 0;
    return encodeKeyFrame({
      frameId: 0,
      timestamp,
      tileDim: TILE_DIM,
      width: frame.width,
      height: frame.height,
      data,
      mime,
    });
  }

  const [lossyData, losslessData] = await Promise.all([
    packer.lossy.pack(diff.lossy, 'image/webp', LOSSY_QUALITY),
    packer.lossless.pack(diff.lossless, textMime, TEXT_QUALITY),
  ]);
  measured.lossyBytes = lossyData?.length ?? 0;
  measured.losslessBytes = losslessData?.length ?? 0;
  return encodeDiffFrame({
    frameId: 0,
    timestamp,
    tileDim: TILE_DIM,
    lossyTiles: Uint16Array.from(diff.lossy),
    losslessTiles: Uint16Array.from(diff.lossless),
    lossyData,
    losslessData,
    lossyMime: 'image/webp',
    losslessMime: textMime,
  });
}

/**
 * Kleine Vorschau, damit sichtbar ist, was übertragen wird.
 *
 * Sie lief bisher bei jedem Frame und tauchte in keiner Messung auf – dabei
 * skaliert sie das ganze Bild herunter, im Hauptthread. Jetzt höchstens alle
 * PREVIEW_INTERVAL Millisekunden, und die Zeit zählt mit.
 */
function drawPreview(frame: { canvas: OffscreenCanvas; width: number; height: number }): void {
  const now = performance.now();
  if (now - lastPreviewAt < PREVIEW_INTERVAL) {
    measured.previewMs = 0;
    return;
  }
  lastPreviewAt = now;

  const canvas = ui.preview;
  const scale = Math.min(320 / frame.width, 200 / frame.height);
  const width = Math.round(frame.width * scale);
  const height = Math.round(frame.height * scale);
  // Nur bei Änderung zuweisen: Jede Zuweisung legt das Canvas neu an und leert es.
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  canvas.getContext('2d')!.drawImage(frame.canvas, 0, 0, width, height);
  measured.previewMs = performance.now() - now;
}

// ---------------------------------------------------------------------------
// Anzeige
// ---------------------------------------------------------------------------

function setStatus(text: string): void {
  ui.status.textContent = text;
}

function updateStats(): void {
  const now = performance.now();
  const elapsed = (now - lastStatsAt) / 1000;
  if (elapsed < 0.5) return;

  const fps = fpsCounter / elapsed;
  const kbps = (sentBytes * 8) / elapsed / 1000;
  fpsCounter = 0;
  sentBytes = 0;
  lastStatsAt = now;

  const total = tiler ? tiler.tileCount : 0;
  const work = sumMs();
  // Seit Aufnahme und Kodierung überlappen, ist nicht die Summe der Posten der
  // Maßstab, sondern der längere der beiden Zweige: Was der Hauptthread tut
  // (Aufnahme, Vergleich, Übernahme) gegen die nebenher laufende Kodierung.
  // Nahe 100 % heißt, dieser Zweig bestimmt den Takt.
  const mainWork = measured.captureMs + measured.diffMs + measured.commitMs + measured.previewMs;
  const bottleneck = Math.max(mainWork, measured.encodeMs);
  const load = measured.cycleMs > 0 ? (bottleneck / measured.cycleMs) * 100 : 0;
  const changed = total > 0 ? (measured.tiles / total) * 100 : 0;

  ui.stats.innerHTML = `
    <div class="headline">
      <b>${fps.toFixed(1)}</b> fps &middot; <b>${kbps.toFixed(0)}</b> kbit/s &middot;
      Auslastung <b>${load.toFixed(0)}%</b>
    </div>
    <div class="muted">
      ${measured.width}&times;${measured.height}
      (Deckel ${capture.maxEdge}${maxEdgeParam !== null ? ', aus URL' : ''}) &middot;
      ${measured.tiles} von ${total} Kacheln (${changed.toFixed(0)} %) &middot;
      Foto ${fmtBytes(measured.lossyBytes)} &middot; Text ${fmtBytes(measured.losslessBytes)}
    </div>

    <div class="metrics">
      ${bar('Aufnahme', measured.captureMs, work)}
      ${bar('Diff', measured.diffMs, work)}
      ${bar('Encoding', measured.encodeMs, work)}
      ${bar('Übernahme', measured.commitMs, work)}
      ${bar('Vorschau', measured.previewMs, work)}
      <span class="label">je 1000 Kacheln</span>
      <span class="value">${per1000(measured.diffMs)}</span>
      <span class="unit">ms Diff</span>
      <span class="note">${per1000(measured.encodeMs)} ms Kodierung</span>

      <span class="label total">Zyklus</span>
      <span class="value total">${measured.cycleMs.toFixed(1)}</span>
      <span class="unit total">ms</span>
      <span class="note">${work.toFixed(1)} ms Arbeit, davon ${measured.encodeMs.toFixed(1)} ms nebenher</span>
    </div>

    <div class="muted">
      Frames ${frameCount} &middot; Vollbilder ${measured.keyframes} &middot;
      abgelehnt ${dropBusy} (Kodierung) / ${dropCongested} (Leitung) &middot;
      übersprungen ${capture.skipped} (Takt)
    </div>
    <div class="muted">
      ${MAX_IN_FLIGHT} gleichzeitig${numberParam('inflight') !== null ? ' (aus URL)' : ''} &middot;
      Text ${textMime.replace('image/', '')} ${
        // PNG kennt keine Qualitätsstufe – dann ist der Atlas verlustfrei und groß.
        textMime === 'image/png' ? '(verlustfrei, Stufe wirkungslos)' : `q=${TEXT_QUALITY}`
      } &middot; Foto webp q=${LOSSY_QUALITY}
    </div>
  `;
}

/** Eine Zeitzeile mit Balken; die Breite zeigt den Anteil an der Gesamtarbeit. */
function bar(label: string, ms: number, total: number): string {
  const share = total > 0 ? Math.min(100, (ms / total) * 100) : 0;
  return `
    <span class="label">${label}</span>
    <span class="value">${ms.toFixed(1)}</span>
    <span class="unit">ms</span>
    <span class="track"><i style="width:${share.toFixed(1)}%"></i></span>
  `;
}

function renderViewerStats(viewers: { id: string; behind: number; relayed: number; coalesced: number; keyframes: number; lastTileCount: number }[]): void {
  if (viewers.length === 0) {
    ui.viewers.textContent = 'keine Zuschauer';
    return;
  }
  ui.viewers.innerHTML =
    `<b>${viewers.length} Zuschauer</b>` +
    viewers
      .map(
        (v) =>
          `<div class="viewer"><code>${v.id}</code> ${v.behind} zurück &middot; ` +
          `${v.relayed} durchgereicht &middot; ${v.coalesced} zusammengefasst &middot; ` +
          `${v.keyframes} Vollbilder &middot; zuletzt ${v.lastTileCount} Kacheln</div>`,
      )
      .join('');
}

/**
 * Zeit je 1000 Kacheln – macht Läufe mit unterschiedlicher Kachelzahl
 * vergleichbar und zeigt, wie viel Rechenleistung die Maschine gerade
 * hergibt. Schwankt dieser Wert zwischen zwei Messungen, lag es nicht am
 * Code, sondern an Fremdlast oder Drosselung.
 */
function per1000(ms: number): string {
  if (measured.tiles === 0) return '–';
  return ((ms / measured.tiles) * 1000).toFixed(1);
}

/** Summe der einzeln gemessenen Posten – der Rest des Zyklus ist Overhead. */
function sumMs(): number {
  return measured.captureMs + measured.diffMs + measured.encodeMs + measured.commitMs + measured.previewMs;
}

function fmtBytes(n: number): string {
  if (n === 0) return '–';
  if (n < 1024) return `${n} B`;
  return `${(n / 1024).toFixed(1)} kB`;
}

// ---------------------------------------------------------------------------
// Start
// ---------------------------------------------------------------------------

ui.start.addEventListener('click', async () => {
  // Vor dem await sperren, nicht danach: Solange der Auswahldialog offen steht –
  // und das sind Sekunden – bliebe der Knopf sonst bedienbar.
  ui.start.disabled = true;
  try {
    await capture.start();
    send({ t: 'sharing', active: true });
    ui.stop.disabled = false;
    setStatus(`teilt ${capture.width}×${capture.height}`);
  } catch (err) {
    ui.start.disabled = false;
    setStatus(`Freigabe abgebrochen: ${(err as Error).message}`);
  }
});

// Aufräumen und Melden übernimmt capture.onStop – für beide Wege gleich.
ui.stop.addEventListener('click', () => capture.stop());

ui.mode.addEventListener('change', () => tiler?.invalidate());

// Bis das Textformat feststeht, nicht starten lassen: Sonst gingen die ersten
// Text-Atlanten als PNG hinaus – ohne wirksame Qualitätsstufe und um ein
// Vielfaches größer. Die Prüfung dauert nur wenige Millisekunden.
ui.start.disabled = true;
detectTextFormat().then((mime) => {
  textMime = mime;
  ui.start.disabled = false;
  updateStats();
});

connect();
